/* eslint-disable react/jsx-props-no-spreading */
import { Avatar, Group, Select, SelectItemProps, SelectProps, Stack, Text as MantineText } from '@mantine/core';
import { forwardRef, useContext, useEffect } from 'react';
import { useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { useAssignableApplicationUsers } from '../../../Service/hooks/useAssignableApplicationUsers';
import type { useBookitupTeam } from '../../../Service/hooks/useBookitupTeam';
import type { Profile } from '../../../Service/ProfileS';

type TaskApplicationUserSelectProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  bookitupTeam?: ReturnType<typeof useBookitupTeam>;
  description?: string;
  disabled?: boolean;
  placeholder?: string;
  profile?: Profile;
  sx?: SelectProps['sx'];
  teamAddonPresent?: boolean;
};

interface ApplicationUserSelectItemProps extends SelectItemProps {
  email: string;
  firstName?: string;
  lastName?: string;
}

type ApplicationUserLabelData = {
  email: string;
  firstName?: string;
  lastName?: string;
};

const getInitials = ({ firstName, lastName, email }: ApplicationUserLabelData) => {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  return initials || email.charAt(0).toUpperCase();
};

const ApplicationUserSelectItem = forwardRef<HTMLDivElement, ApplicationUserSelectItemProps>(
  ({ label, email, firstName, lastName, ...rest }: ApplicationUserSelectItemProps, ref) => (
    <div ref={ref} {...rest}>
      <Group noWrap spacing="sm">
        <Avatar size={32} radius="xl">
          {getInitials({ firstName, lastName, email })}
        </Avatar>
        <Stack spacing={0}>
          <MantineText size="sm" weight={500}>
            {label}
          </MantineText>
          {email && label !== email && (
            <MantineText size="xs" color="dimmed">
              {email}
            </MantineText>
          )}
        </Stack>
      </Group>
    </div>
  ),
);

const TaskApplicationUserSelect = ({
  value,
  onChange,
  bookitupTeam: bookitupTeamProp,
  description = 'Zugewiesen an',
  disabled = false,
  placeholder,
  profile,
  sx = { marginBottom: 10 },
  teamAddonPresent: teamAddonPresentProp,
}: TaskApplicationUserSelectProps) => {
  const { teamAddonPresent: contextTeamAddonPresent } = useBookitupSubscription();
  const teamAddonPresent = teamAddonPresentProp ?? contextTeamAddonPresent;
  const { bookitupTeam } = useContext(ProfileContext);
  const activeBookitupTeam = bookitupTeamProp ?? bookitupTeam;
  const loading = activeBookitupTeam.loading && !activeBookitupTeam.sharesLoaded;
  const data = useAssignableApplicationUsers(activeBookitupTeam, profile);

  useEffect(() => {
    if (!teamAddonPresent) return;

    activeBookitupTeam.ensureSharesLoaded().catch(() => undefined);
  }, [activeBookitupTeam, teamAddonPresent]);

  if (!teamAddonPresent) return null;

  return (
    <Select
      clearable
      searchable
      disabled={loading || disabled}
      value={value}
      onChange={onChange}
      data={data}
      itemComponent={ApplicationUserSelectItem}
      description={description}
      placeholder={loading ? 'Teammitglieder werden geladen ...' : placeholder ?? 'Person auswählen'}
      nothingFound="Keine Personen gefunden"
      maxDropdownHeight={280}
      sx={sx}
    />
  );
};

export default TaskApplicationUserSelect;
